import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { getSafeExternalUrl } from "@WhiteCap/Common/Utillities/SafeRedirect";
import * as cssLinter from "css";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    searchDomain = "searchDomain",
    searchPath = "searchPath",
    placeholderText = "placeholderText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.placeholderText]: string;
        [fields.searchDomain]: string;
        [fields.searchPath]: string;
    };
}

export interface CoveoSearchInputStyles {
    input?: TextFieldPresentationProps;
}

export const coveoSearchInputStyles: CoveoSearchInputStyles = {
    input: {
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css``,
            formInputWrapper: css`
                margin-top: 0px !important;
                button[class^="StyledButton"] {
                    padding: 0px !important;
                    display: flex;
                    span[class^="IconWrapper"] {
                        background-color: #ffc107;
                        border-radius: 5px;
                        height: 34px;
                        top: 4px;
                        right: 4px;
                        svg {
                            path {
                                stroke: black !important;
                            }
                        }
                    }
                }
            `,
            inputSelect: css`
                background: white;
                border: none;
                height: 42px !important;
                padding: 0 13px;
                &::placeholder {
                    font-family: Arial, Helvetica, sans-serif;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 16px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
                &:focus {
                    border: none;
                    padding: 0 13px;
                }
            `,
        },
    },
};

const defaultCustomCss = `.search-input{
}

.search-input-wrapper{
}

.search-button{
}

.search-input-field{
}`;

const defaultSearchDomain = "https://dev-omni.whitecap.com";
const defaultSearchPath = "/en-us/search";
const defaultPlaceholderText = "Search by name, brand, product id...";

export const HeaderSearchInput: React.FC<OwnProps> = ({ id, fields }) => {
    const styles = useMergeStyles("headerSearchInput", coveoSearchInputStyles as any);
    const [query, setQuery] = useState("");

    const searchDomain = fields.searchDomain || defaultSearchDomain;
    const searchPath = fields.searchPath || defaultSearchPath;
    const placeholderText = fields.placeholderText || defaultPlaceholderText;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const handleSearch = () => {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length === 0) {
            return;
        }

        const encodedQuery = encodeURIComponent(trimmedQuery);
        const searchUrl = `${searchDomain}${searchPath}?query=${encodedQuery}`;

        const safeSearchUrl = getSafeExternalUrl(searchUrl);
        if (safeSearchUrl) {
            window.location.href = safeSearchUrl;
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const searchId = `coveoSearch-${id}`;

    return (
        <StyledWrapper {...customCssWrapper} className="search-input-wrapper">
            <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                Site Search
            </VisuallyHidden>
            <TextField
                {...styles.input}
                id={searchId}
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholderText}
                autoComplete="off"
                aria-label="Search"
                data-test-selector="coveoSearchInputTextField"
                className="search-input-field"
                iconClickableProps={{ onClick: handleSearch, tabIndex: undefined }}
                style={{ paddingRight: "45px" }}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderSearchInput,
    definition: {
        group: "Header",
        displayName: "Coveo Search Input",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.searchDomain,
                displayName: "Search Domain",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: defaultSearchDomain,
                tooltip: "The base domain for the Coveo search (e.g., https://dev-omni.whitecap.com)",
            },
            {
                name: fields.searchPath,
                displayName: "Search Path",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: defaultSearchPath,
                tooltip: "The URL path for search (e.g., /en-us/search)",
            },
            {
                name: fields.placeholderText,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: defaultPlaceholderText,
                tooltip: "Placeholder text shown in the search input",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
